import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { slopeGradeLabel, formatContourIntervalLabel, } from '../utils/legendData';
import { formatElevationLabel } from '../utils/units';
import { TWI_LOW_RGB, TWI_HIGH_RGB, rgbHex } from './MeasureTool/elevationColors';
import { getPlacemarkIconCanvas } from '../utils/placemarkIcons/placemarkIconRenderer';
// Renders the placemark's ACTUAL map billboard (coloured badge + glyph) as a
// small image, so the legend symbol matches exactly what's on the map — not a
// glyph-only preview (which is unreadable on a white sheet for light colours).
const PlacemarkGlyph = ({ icon, color }) => {
    let src = null;
    try {
        // No shadow in the legend — it's clutter that shrinks the readable glyph.
        src = getPlacemarkIconCanvas(icon, color, 1.0, false).toDataURL();
    }
    catch (_a) {
        src = null;
    }
    if (!src) {
        return _jsx("span", { style: { width: 11, height: 11, background: color, border: '1px solid rgba(0,0,0,0.3)', borderRadius: '50%' } });
    }
    // Badge canvas is 48×56; preserve that aspect at legend size.
    return _jsx("img", { src: src, alt: "", style: { width: 15, height: 17, objectFit: 'contain' } });
};
// Print-styled legend (white sheet) rendered in the sheet's right-hand column.
// Only sections backed by a currently-visible layer are shown, so the legend
// adapts to how many layers are active.
const INK = '#2f3027';
const BLUES = `linear-gradient(to right, ${rgbHex(TWI_LOW_RGB)}, ${rgbHex(TWI_HIGH_RGB)})`;
// #rrggbb (or #rgb) → rgba() string. Falls back to the input on parse failure.
const hexToRgba = (hex, alpha) => {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || '');
    if (!m)
        return hex;
    let h = m[1];
    if (h.length === 3)
        h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
// Continuous Blues gradient bar with end labels (TWI / flow accumulation).
const GradientBar = ({ low, high }) => (_jsxs("div", { children: [_jsx("div", { style: { height: 10, borderRadius: 2, background: BLUES, border: '1px solid rgba(0,0,0,0.25)' } }), _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', fontSize: 10, color: INK, marginTop: 3 }, children: [_jsx("span", { children: low }), _jsx("span", { children: high })] })] }));
const Swatches = ({ items }) => (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: 4 }, children: items.map((it, i) => (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [_jsx("span", { style: {
                    width: 14,
                    height: 14,
                    background: it.color,
                    border: '1px solid rgba(0,0,0,0.25)',
                    borderRadius: 2,
                    flexShrink: 0,
                } }), _jsx("span", { style: { fontSize: 11, color: INK }, children: it.title })] }, i))) }));
// A glyph that reads as the feature's on-map shape: polygons get a filled
// square (outline in the outline colour), placemarks a dot, and the line-like
// features (paths, measurements, highlighted contours) a coloured rule. All sit
// in a fixed-width box so the names line up.
const FeatureGlyph = ({ item }) => {
    var _a;
    const box = {
        width: 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    };
    if (item.kind === 'polygon') {
        // Match the map: translucent fill + a solid outline in the polygon's own
        // outline colour (falling back to the fill colour, never black).
        const fill = hexToRgba(item.color, (_a = item.fillOpacity) !== null && _a !== void 0 ? _a : 0.35);
        const outline = item.outlineColor || item.color;
        return (_jsx("span", { style: box, children: _jsx("span", { style: {
                    width: 13,
                    height: 13,
                    background: fill,
                    border: `1.5px solid ${outline}`,
                    borderRadius: 2,
                } }) }));
    }
    if (item.kind === 'placemark') {
        return (_jsx("span", { style: box, children: _jsx(PlacemarkGlyph, { icon: item.icon, color: item.color }) }));
    }
    // path / measurement / contour → a coloured rule
    return (_jsx("span", { style: box, children: _jsx("span", { style: { width: 16, height: 3, background: item.color, borderRadius: 1 } }) }));
};
const FeatureList = ({ items }) => (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: 4 }, children: items.map((it, i) => (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [_jsx(FeatureGlyph, { item: it }), _jsx("span", { style: {
                    fontSize: 11,
                    // This row is overflow:hidden for its ellipsis, so it needs the
                    // constant bottom slack the PDF exporter's baseline error demands —
                    // at 11px, line-height alone is nowhere near enough. See the note
                    // in PrintTitleBlock.
                    lineHeight: 1.5,
                    paddingBottom: 4,
                    color: INK,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }, children: it.name })] }, i))) }));
// Slope swatches carry the grade % (steepness) alongside the class label.
const SlopeSwatches = ({ items }) => (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: 4 }, children: items.map((it, i) => {
        const grade = slopeGradeLabel(it.title);
        return (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [_jsx("span", { style: {
                        width: 14,
                        height: 14,
                        background: it.color,
                        border: '1px solid rgba(0,0,0,0.25)',
                        borderRadius: 2,
                        flexShrink: 0,
                    } }), _jsxs("span", { style: { fontSize: 11, color: INK }, children: [it.title, grade ? _jsxs("span", { style: { color: '#8a8b7e' }, children: [" \u00B7 ", grade] }) : null] })] }, i));
    }) }));
const Section = ({ title, children }) => (_jsxs("div", { style: { marginBottom: 12 }, children: [_jsx("div", { style: { fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: INK, marginBottom: 5 }, children: title }), children] }));
// `system` is the sheet-wide unit setting (the print panel's "Units" control),
// so the legend reads in the same system as the scale bar.
const PrintLegend = ({ data, system }) => (_jsxs("div", { style: {
        padding: '12px 14px',
        height: '100%',
        overflow: 'hidden',
        fontFamily: 'Helvetica, Arial, sans-serif',
    }, children: [_jsx("div", { style: { fontSize: 12, fontWeight: 700, letterSpacing: 1, color: INK, marginBottom: 10 }, children: "LEGEND" }), data.features.length > 0 && (_jsx(Section, { title: "Features", children: _jsx(FeatureList, { items: data.features }) })), (data.elevation || data.contourIntervals.length > 0 || data.monochromeColors.length > 0) && (_jsxs(Section, { title: "Elevation", children: [data.elevation && (_jsxs("div", { style: { display: 'flex', gap: 8, alignItems: 'stretch' }, children: [_jsx("div", { style: {
                                width: 18,
                                height: 90,
                                background: data.elevation.gradientCss,
                                border: '1px solid rgba(0,0,0,0.25)',
                            } }), _jsxs("div", { style: {
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                fontSize: 10,
                                color: INK,
                            }, children: [_jsx("span", { children: formatElevationLabel(data.elevation.max, system) }), _jsx("span", { children: formatElevationLabel(data.elevation.mid, system) }), _jsx("span", { children: formatElevationLabel(data.elevation.min, system) })] })] })), data.monochromeColors.map((c, i) => (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6, marginTop: data.elevation ? 6 : 0 }, children: [_jsx("span", { style: { width: 18, height: 3, background: c, flexShrink: 0 } }), _jsx("span", { style: { fontSize: 11, color: INK }, children: "Contours (monochrome)" })] }, i))), data.contourIntervals.length > 0 && (_jsxs("div", { style: { fontSize: 11, color: '#8a8b7e', marginTop: 6 }, children: ["Interval:", ' ', data.contourIntervals.map((e) => formatContourIntervalLabel(e, system)).join(' · ')] }))] })), data.slope.length > 0 && (_jsx(Section, { title: "Slope", children: _jsx(SlopeSwatches, { items: data.slope }) })), data.aspect.length > 0 && (_jsx(Section, { title: "Aspect", children: _jsx(Swatches, { items: data.aspect }) })), data.geomorphons.length > 0 && (_jsx(Section, { title: "Geomorphons", children: _jsx(Swatches, { items: data.geomorphons }) })), (data.twi || data.twiPercentiles.length > 0) && (_jsxs(Section, { title: "Wetness (TWI)", children: [data.twi && _jsx(GradientBar, { low: "Drier", high: "Wetter" }), data.twiPercentiles.length > 0 && (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: data.twi ? 6 : 0 }, children: data.twiPercentiles.map((p) => (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [_jsx("span", { style: {
                                    width: 14,
                                    height: 14,
                                    background: rgbHex(TWI_HIGH_RGB),
                                    border: '1px solid rgba(0,0,0,0.25)',
                                    borderRadius: 2,
                                    flexShrink: 0,
                                } }), _jsxs("span", { style: { fontSize: 11, color: INK }, children: [p.label, " ", _jsxs("span", { style: { color: '#8a8b7e' }, children: ["\u00B7 ", p.pct, "th pct"] })] })] }, p.pct))) }))] })), data.flow && (_jsx(Section, { title: "Flow Accumulation", children: _jsx(GradientBar, { low: "Less", high: "More" }) }))] }));
export default PrintLegend;
